<template>
  <div class="bar-chart-card">
    <!-- Header -->
    <div class="bar-chart-card__header">
      <div class="header-left">
        <h2 class="bar-chart-card__title">{{ title }}</h2>
        <p class="bar-chart-card__subtitle">{{ subtitle }}</p>
      </div>
      <div class="header-right">
        <span class="bar-chart-card__total">Total: {{ total }}</span>
      </div>
    </div>

    <!-- Chart / No data -->
    <div class="bar-chart-card__canvas-wrapper" :class="{ 'is-empty': total === 0 }">
      <!-- No Data -->
      <div v-if="total === 0" class="bar-chart-no-data" role="status" aria-live="polite">
        <div class="bar-no-data-illustration">
          <ion-icon name="bar-chart-outline"></ion-icon>
        </div>
        <h3 class="bar-no-data-title">Sin datos en este rango</h3>
        <p class="bar-no-data-text">Ajusta fechas o cambia la línea para ver información.</p>
      </div>

      <!-- Canvas -->
      <canvas v-else ref="chartRef"></canvas>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, onBeforeUnmount, watch } from 'vue'
import {
  Chart,
  BarController,
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend
} from 'chart.js'

Chart.register(BarController, BarElement, CategoryScale, LinearScale, Tooltip, Legend)

function hexToRgba(hex, alpha = 0.12) {
  const h = hex.replace('#','');
  const bigint = parseInt(h.length === 3
    ? h.split('').map(c => c + c).join('')
    : h, 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

export default {
  name: 'BarChart',
  props: {
    title: { type: String, default: 'Título del gráfico' },
    subtitle: { type: String, default: 'Subtítulo del gráfico' },
    total: { type: Number, default: 0 },
    labels: { type: Array, required: true },
    data: { type: Array, required: true },
    colors: { type: Array, required: true }
  },
  setup(props) {
    const chartRef = ref(null)
    let chartInstance = null

    const renderChart = () => {
      if (!chartRef.value) return

      chartInstance = new Chart(chartRef.value, {
        type: 'bar',
        data: {
          labels: props.labels,
          datasets: [{
            label: 'Total',
            data: props.data,
            backgroundColor: props.colors.map(c => hexToRgba(c, 0.12)),
            borderColor: props.colors,
            borderWidth: 2,
            borderRadius: 8,
            borderSkipped: 'bottom',
            barPercentage: 0.6,
            categoryPercentage: 0.7,
            hoverBackgroundColor: props.colors.map(c => hexToRgba(c, 0.2)),
            hoverBorderColor: props.colors,
            hoverBorderWidth: 2
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          scales: {
            x: {
              ticks: {
                color: '#64748b',
                font: { family: 'Manrope', size: 12 }
              },
              grid: { display: false }
            },
            y: {
              beginAtZero: true,
              ticks: {
                color: '#64748b',
                font: { family: 'Manrope', size: 12 }
              },
              grid: {
                color: 'rgba(100,116,139,0.1)',
                borderDash: [4, 4]
              }
            }
          },
          plugins: {
            legend: { display: false },
            tooltip: {
              backgroundColor: '#1e293b',
              titleFont: { family: 'Manrope', size: 13 },
              bodyFont: { family: 'Manrope', size: 12 },
              padding: 10,
              callbacks: {
                label: (tooltipItem) => {
                  return ` ${tooltipItem.formattedValue} contactos`
                }
              }
            }
          }
        }
      })
    }

    onMounted(() => renderChart())

    watch(
      () => [props.labels, props.data, props.colors],
      ([newLabels, newData, newColors]) => {
        if (!chartInstance) return
        chartInstance.data.labels = newLabels
        chartInstance.data.datasets[0].data = newData
        chartInstance.data.datasets[0].backgroundColor = newColors.map(c => hexToRgba(c, 0.12))
        chartInstance.data.datasets[0].borderColor = newColors
        chartInstance.update()
      },
      { deep: true }
    )

    onBeforeUnmount(() => {
      if (chartInstance) chartInstance.destroy()
    })

    return { chartRef }
  }
}
</script>

<style scoped>
.bar-chart-card {
  background-color: #ffffff;
  border-radius: 1.25rem;
  padding: 1.5rem;
  box-shadow: 0 4px 16px rgba(15, 23, 42, 0.06);
  border: 1px solid #e2e8f0;
  display: flex;
  flex-direction: column;
  margin-top: 2rem;
  font-family: 'Manrope', sans-serif;
  max-height: 360px;
}

.bar-chart-card__header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  flex-wrap: wrap;
  margin-bottom: 1rem;
}

.header-left {
  display: flex;
  flex-direction: column;
  text-align: left;
}

.header-right {
  font-size: 0.95rem;
  font-weight: 600;
  color: #0f172a;
  background: #f1f5f9;
  padding: 0.4rem 0.8rem;
  border-radius: 0.5rem;
  border: 1px solid #e2e8f0;
}

.bar-chart-card__title {
  font-size: 1.25rem;
  font-weight: 700;
  color: #0f172a;
  margin: 0;
}

.bar-chart-card__subtitle {
  font-size: 0.95rem;
  font-weight: 500;
  color: #64748b;
  margin: 0;
}

/* Altura */
.bar-chart-card__canvas-wrapper {
  position: relative;
  width: 100%;
  height: 280px;
}

.bar-chart-card__canvas-wrapper.is-empty {
  height: 150px;
}

/* ===== No Data ====== */
.bar-chart-no-data {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: .5rem;
  text-align: center;
  background: linear-gradient(180deg, #f8fafc 0%, #ffffff 100%);
  border: 2px dashed #e2e8f0;
  border-radius: .75rem;
  padding: 1rem;
}

.bar-no-data-illustration {
  width: 56px;
  height: 56px;
  border-radius: 9999px;
  background: #eef2ff;
  display: grid;
  place-items: center;
  box-shadow: inset 0 0 0 1px #e2e8f0;
}

.bar-no-data-illustration ion-icon {
  font-size: 1.4rem;
  color: #64748b;
}

.bar-no-data-title {
  margin: 0;
  font-weight: 700;
  font-size: .95rem;
  color: #0f172a;
}

.bar-no-data-text {
  margin: 0;
  font-weight: 500;
  font-size: .85rem;
  color: #64748b;
}
</style>